import moment from 'moment-timezone';

import { removeMask } from './mask-helper';

const CPF_BLACKLIST = [
    '00000000000',
    '11111111111',
    '22222222222',
    '33333333333',
    '44444444444',
    '55555555555',
    '66666666666',
    '77777777777',
    '88888888888',
    '99999999999',
    '12345678909',
];

const CNPJ_BLACKLIST = [
    '00000000000000',
    '11111111111111',
    '22222222222222',
    '33333333333333',
    '44444444444444',
    '55555555555555',
    '66666666666666',
    '77777777777777',
    '88888888888888',
    '99999999999999',
];

/* eslint-disable max-len */
const REGEX_URL = /^https?:\/\/([\w-.:]+)(\/?|\/.+)$/;
const REGEX_EMAIL = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
const REGEX_CEP = /^[0-9]{8}$/;
const REGEX_TELEFONE = /^[0-9]{10,11}$/;
// E.164 (UIT-T): sinal "+", primeiro digito 1-9 e no maximo 15 digitos no total.
// Testado contra o valor cru, nao contra removeMask, que apagaria o "+".
const REGEX_TELEFONE_E164 = /^\+[1-9]\d{1,14}$/;
const REGEX_OUTRO_DOCUMENTO = /^[A-Z0-9]{6,9}$/;
/* eslint-enable */

export const validateName = value => {
    if (typeof value === 'string') {
        const parts = value.trim().split(' ');
        if (parts.length >= 2) {
            const partsAreValid = parts.reduce((valid, part) => (
                valid && part.length >= 2
            ), true);
            if (partsAreValid) {
                return undefined;
            }
        }
    }
    return 'Informe o nome completo';
};

export const validateCheckbox = value => {
    return value ? undefined : 'Este campo precisa ser marcado.';
};

export const validateEmail = value => (
    REGEX_EMAIL.test(value) ? undefined : 'E-mail inválido.'
);

// Normaliza antes de testar, como validateCep e validatePhone fazem: o documento pode
// chegar do banco com espaço ou pontuação — o modal de check-in grava o que foi digitado,
// sem parse — e REGEX_OUTRO_DOCUMENTO só aceita [A-Z0-9].
export const validatePassport = value => (
    REGEX_OUTRO_DOCUMENTO.test(removeMask(value).toUpperCase())
        ? undefined
        : 'Documento inválido.'
);

export const validateNotEmpty = value => {
    let valid;
    if (typeof value === 'string') {
        valid = Boolean(value.trim());
    } else if (Array.isArray(value)) {
        valid = Boolean(value.length);
    } else {
        valid = Boolean(value);
    }
    return valid ? undefined : 'Campo obrigatório';
};

const isValidNumber = value => {
    switch (typeof value) {
        case 'number':
            return Number.isFinite(value);
        case 'string':
            return /^[0-9,.]+$/.test(value);
        default:
            return false;
    }
};

export const validateNumber = value => {
    return isValidNumber(value) ? undefined : 'Deve conter apenas números';
};

const verifierDigitCPF = value => {
    const numbers = value
        .split('')
        .map(number => Number(number));
    const modulus = numbers.length + 1;
    const multiplied = numbers.map((number, index) => number * (modulus - index));
    const mod = multiplied.reduce((buffer, number) => buffer + number) % 11;
    return (mod < 2 ? 0 : 11 - mod);
};

export const validateCPF = value => {
    const stripped = (value || '').replace(/[^\d]/g, '');

    if (stripped && stripped.length === 11 && CPF_BLACKLIST.indexOf(stripped) === -1) {
        let numbers = stripped.substr(0, 9);
        numbers += verifierDigitCPF(numbers);
        numbers += verifierDigitCPF(numbers);

        if (numbers.substr(-2) === stripped.substr(-2)) {
            return undefined;
        }
    }
    return 'Número de CPF inválido';
};

export const validateUrl = value => (
    REGEX_URL.test(value) ? undefined : 'URL inválida'
);

export const validateDate = value => {
    return moment(value, 'DD/MM/YYYY', true).isValid()
        ? undefined
        : 'Informe uma data válida no formato DD/MM/AAAA';
};

export const validateCep = value => (
    REGEX_CEP.test(removeMask(value))
        ? undefined
        : 'Informe um CEP válido'
);

// O segundo parametro vem do react-final-form via wrapFormField (form-helper.js), que
// chama validate(value, values). Formularios sem a flag caem no ramo nacional.
export const validatePhone = (value, values) => {
    if (values?.isForeigner) {
        return REGEX_TELEFONE_E164.test(value)
            ? undefined
            : 'Informe um telefone internacional no formato +5511987654321';
    }

    return REGEX_TELEFONE.test(removeMask(value))
        ? undefined
        : 'Informe um telefone válido';
};

// Wrappers opcionais: só validam quando há valor. Os campos de contato (telefone/e-mail)
// são opcionais no check-in; vazio deve ser considerado válido para não bloquear o submit.
export const validateOptionalEmail = value => (value ? validateEmail(value) : undefined);

export const validateOptionalPhone = (value, values) => (value ? validatePhone(value, values) : undefined);

// CNPJ alfanumérico (Serpro, vigente a partir de 2026): 12 posições base
// alfanuméricas (0-9, A-Z) + 2 dígitos verificadores numéricos.
// Mantém apenas caracteres válidos e padroniza em maiúsculas.
const stripCNPJ = value => (value || '').toUpperCase().replace(/[^0-9A-Z]/g, '');

// 12 caracteres base alfanuméricos seguidos de 2 dígitos verificadores numéricos.
const REGEX_CNPJ = /^[0-9A-Z]{12}[0-9]{2}$/;

// Valor numérico de cada caractere no cálculo do DV: código ASCII - 48.
// Assim '0'-'9' → 0-9 e 'A'-'Z' → 17-42. Para CNPJ puramente numérico o
// resultado é idêntico a Number(char), então a mesma fórmula atende os dois formatos.
const charValueCNPJ = char => char.charCodeAt(0) - 48;

const verifierDigitCNPJ = numbers => {
    const reverse = numbers.split('').reduce((buffer, char) => (
        [charValueCNPJ(char), ...buffer]
    ), []);

    let index = 2;
    const sum = reverse.reduce((buffer, number) => {
        const result = buffer + (number * index);
        index = (index === 9 ? 2 : index + 1);
        return result;
    }, 0);

    const mod = sum % 11;
    return (mod < 2 ? 0 : 11 - mod);
};

// Base (12 primeiras posições) formada por um único caractere repetido:
// ex. 000000000000, AAAAAAAAAAAA. Nunca são CNPJs reais, em nenhum formato.
const REGEX_CNPJ_BASE_REPETIDA = /^(.)\1{11}$/;

export const validateCNPJ = value => {
    const stripped = stripCNPJ(value);
    const base = stripped.substr(0, 12);

    if (REGEX_CNPJ.test(stripped)
        && !REGEX_CNPJ_BASE_REPETIDA.test(base)
        && CNPJ_BLACKLIST.indexOf(stripped) === -1) {
        let numbers = stripped.substr(0, 12);
        numbers += verifierDigitCNPJ(numbers);
        numbers += verifierDigitCNPJ(numbers);

        if (numbers.substr(-2) === stripped.substr(-2)) {
            return undefined;
        }
    }

    return 'Número de CNPJ inválido';
};

export const validateCPFCNPJ = value => {
    // 09963443907        -> CPF (11 dígitos numéricos)
    // 04294897002701     -> CNPJ numérico (14 dígitos)
    // 12ABC34501DE35     -> CNPJ alfanumérico (12 base + 2 dígitos)
    const cpfDigits = (value || '').replace(/[^\d]/g, '');

    // CPF: exatamente 11 dígitos numéricos.
    if (cpfDigits.length === 11 && validateCPF(value) === undefined) {
        return undefined;
    }

    // CNPJ (numérico ou alfanumérico): 14 posições após normalizar.
    if (stripCNPJ(value).length === 14 && validateCNPJ(value) === undefined) {
        return undefined;
    }

    return 'Número de CPF ou CNPJ inválido';
};

export const validateCreditValue = value => {
    if (value >= 1) {
        return undefined;
    }

    return 'O valor mínimo a ser declardo é de R$ 1,00.';
};

export const areObjectsEqual = (object1, object2) => {
    const obj1 = object1 || {};
    const obj2 = object2 || {};
    /* eslint-disable no-prototype-builtins, no-restricted-syntax, guard-for-in, eqeqeq */

    // eslint-disable-next-line no-unused-vars
    for (const p in obj1) {
        switch (typeof obj1[p]) {
            case 'object':
                if (!areObjectsEqual(obj1[p], obj2[p])) return false;
                break;
            default:
                if (obj1[p] != obj2[p]) return false;
        }
    }

    // eslint-disable-next-line no-unused-vars
    for (const p in obj2) {
        switch (typeof obj2[p]) {
            case 'object':
                if (!areObjectsEqual(obj2[p], obj1[p])) return false;
                break;
            default:
                if (obj1[p] != obj2[p]) return false;
        }
    }

    /* eslint-enable */
    return true;
};
